<template>
  <div class="pa-4">
    <!-- Info alert -->
    <v-alert
      v-if="!hasFormats"
      class="mb-4"
      type="info"
      variant="tonal"
      icon="mdi-information">
      This asset does not have any format variants yet. Formats are different
      aspect ratios or resolutions of the same media (e.g., 16:9, 9:16, 4:3
      versions).
    </v-alert>

    <v-alert
      v-else
      class="mb-4"
      type="info"
      variant="tonal"
      icon="mdi-aspect-ratio">
      This asset has {{ formats.length }} format{{
        formats.length !== 1 ? 's' : ''
      }}. Formats are purely aspect ratio/resolution variants without
      conditional logic.
    </v-alert>

    <!-- Save indicator -->
    <v-alert
      v-if="isSaving"
      class="mb-2"
      type="info"
      density="compact"
      variant="tonal">
      <v-progress-circular
        indeterminate
        size="16"
        width="2"
        class="mr-2"></v-progress-circular>
      Saving changes...
    </v-alert>

    <!-- Original Asset Info -->
    <v-card
      class="mb-3"
      variant="tonal"
      color="primary">
      <v-card-title class="d-flex align-center">
        <v-icon
          class="mr-2"
          color="primary">
          mdi-file-outline
        </v-icon>
        Original Asset
      </v-card-title>
      <v-card-text>
        <div class="d-flex flex-column">
          <div class="mb-2">
            <strong>Filename:</strong>
            {{ userEditObj.name }}
          </div>
          <div class="mb-2">
            <strong>Type:</strong>
            {{ userEditObj.asset_type }}
          </div>
          <div
            v-if="userEditObj.info?.width && userEditObj.info?.height"
            class="mb-2">
            <strong>Dimensions:</strong>
            {{ userEditObj.info.width }} x
            {{ userEditObj.info.height }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Formats List Header -->
    <div class="mb-3 d-flex align-center justify-space-between">
      <h3 class="text-h6">Format Variants</h3>
      <v-chip
        v-if="hasFormats"
        color="primary"
        variant="tonal">
        {{ formats.length }} format{{ formats.length !== 1 ? 's' : '' }}
      </v-chip>
    </div>

    <!-- Formats List -->
    <div v-if="hasFormats">
      <v-card
        v-for="(format, index) in formats"
        :key="index"
        class="mb-3"
        variant="outlined">
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon
              class="mr-2"
              :color="getFormatIconColor(format.format)">
              {{ getFormatIcon(format.format) }}
            </v-icon>
            <span>{{ format.format || 'Unknown Format' }}</span>
          </div>
          <v-btn
            icon="mdi-delete"
            size="small"
            variant="text"
            color="error"
            @click="confirmDeleteFormat(index)"></v-btn>
        </v-card-title>

        <v-card-text>
          <div class="mb-2">
            <strong>Filename:</strong>
            {{ format.filename }}
          </div>
          <div class="mb-2">
            <strong>Path:</strong>
            <code>{{ format.filepath }}</code>
          </div>
          <div
            v-if="format.created_at"
            class="mb-2">
            <strong>Created:</strong>
            {{ formatDate(format.created_at) }}
          </div>
          <div
            v-if="format.tags && format.tags.length > 0"
            class="mb-2">
            <strong>Tags:</strong>
            <v-chip
              v-for="tagUid in format.tags"
              :key="tagUid"
              size="small"
              class="mr-1 mb-1"
              color="primary"
              variant="tonal">
              {{ getTagName(tagUid) }}
            </v-chip>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Empty state -->
    <v-card
      v-else
      class="pa-8 text-center"
      variant="outlined">
      <v-icon
        size="64"
        color="grey-lighten-1"
        class="mb-4">
        mdi-aspect-ratio
      </v-icon>
      <div class="text-h6 mb-2">No Format Variants</div>
      <div class="text-body-2 text-grey">
        Format variants are different aspect ratios or resolutions of the same
        media.
        <br />
        For conditional selection, use AssetGroups with asset-level conditions
        instead.
      </div>
    </v-card>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="deleteDialog"
      max-width="500">
      <v-card>
        <v-card-title>Delete Format?</v-card-title>
        <v-card-text>
          Are you sure you want to delete this format variant? This action
          cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="deleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            @click="deleteFormat">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { computed, ref, watch, inject } from 'vue';

const props = defineProps({
  userEditObj: {
    type: Object,
    required: true,
  },
});

// Inject services provided by a parent component
const $http = inject('$http');
const deluge = inject('deluge');

const isSaving = ref(false);
const deleteDialog = ref(false);
const formatToDelete = ref(null);

const hasFormats = computed(() => {
  return (
    props.userEditObj?.extra_info?.formats &&
    props.userEditObj.extra_info.formats.length > 0
  );
});

const formats = computed(() => {
  return props.userEditObj?.extra_info?.formats || [];
});

const getTagName = (tagUid) => {
  const tag = deluge.tags?.[tagUid];
  return tag?.name || tagUid;
};

const getFormatIcon = (format) => {
  const formatLower = (format || '').toLowerCase();
  if (formatLower.includes('16:9') || formatLower.includes('landscape'))
    return 'mdi-monitor';
  if (formatLower.includes('9:16') || formatLower.includes('portrait'))
    return 'mdi-cellphone';
  if (formatLower.includes('4:3')) return 'mdi-television-classic';
  if (formatLower.includes('1:1') || formatLower.includes('square'))
    return 'mdi-square-outline';
  return 'mdi-aspect-ratio';
};

const getFormatIconColor = (format) => {
  const formatLower = (format || '').toLowerCase();
  if (formatLower.includes('16:9')) return 'blue';
  if (formatLower.includes('9:16')) return 'purple';
  if (formatLower.includes('4:3')) return 'orange';
  if (formatLower.includes('1:1')) return 'green';
  return 'grey';
};

function formatDate(dateString) {
  if (!dateString) return 'Unknown';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date'; // Check for valid date
    return date.toLocaleString();
  } catch {
    return 'Invalid Date';
  }
}

const confirmDeleteFormat = (index) => {
  formatToDelete.value = index;
  deleteDialog.value = true;
};

const deleteFormat = () => {
  if (formatToDelete.value === null) return;

  const index = formatToDelete.value;
  props.userEditObj.extra_info.formats.splice(index, 1);

  // Close dialog and reset
  deleteDialog.value = false;
  formatToDelete.value = null;

  // The watcher will pick up this change and trigger the auto-save
};

// Auto-save when formats change
let saveTimeout = null;
watch(
  () => props.userEditObj?.extra_info?.formats,
  (newVal, oldVal) => {
    // Skip if initial load or no change
    if (!oldVal || JSON.stringify(newVal) === JSON.stringify(oldVal)) {
      return;
    }

    // Debounce saves
    clearTimeout(saveTimeout);
    saveTimeout = setTimeout(async () => {
      isSaving.value = true;
      try {
        await $http('PUT', `/update_asset_formats`, {
          asset_uid: props.userEditObj.uid,
          formats: props.userEditObj.extra_info.formats,
        });
      } catch (error) {
        console.error('Error saving formats:', error);
      } finally {
        isSaving.value = false;
      }
    }, 500); // 500ms debounce
  },
  { deep: true }
);
</script>

<style scoped>
code {
  background-color: rgba(0, 0, 0, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.875rem;
  word-break: break-all;
}
</style>
